<!--
  ProductQrShareDialog -- store-editor "share this product" QR code.

  Boulders: Storefront color deep-link + QR share code (pebble 2 of 2)
  https://trello.com/c/PWUeRl0u

  Pebble 1 (PDP ?color= deep link, shipped PR #2559) built the URL shape.
  This dialog is the tool a distributor uses to HAND OUT that link: pick a
  color, see the QR update live, read exactly what it links to (store,
  product, color -- named, not vague), and copy the URL as a fallback for
  codes that will not scan.

  The QR + resolved URL are BACKEND-rendered (GET
  /api/campaign_product/<id>/qr?color=<name>) -- the store slug and
  category slug are not available on the frontend CampaignProduct payload
  (see ProductCard.vue), and the URL MUST go through the server's
  storefront_path_url helper so a printed code can never point at a
  different host than an emailed link.
-->
<template>
  <div class="d-flex justify-center">
    <v-dialog v-model="localOpen" max-width="480">
      <v-card data-testid="product-qr-share-dialog">
        <v-card-title class="text-h6">
          Share this product
        </v-card-title>

        <v-card-text>
          <v-select
            v-if="colorOptions.length > 0"
            v-model="selectedColor"
            :items="colorOptions"
            label="Color"
            data-testid="product-qr-color-select"
            @update:modelValue="fetchQr"
          />

          <div
            v-if="loading"
            class="d-flex justify-center py-6"
            data-testid="product-qr-loading"
          >
            <v-progress-circular indeterminate color="primary" />
          </div>

          <div
            v-else-if="error"
            class="text-error py-2"
            data-testid="product-qr-error"
          >
            {{ error }}
          </div>

          <template v-else-if="svg">
            <!--
              Rendered with v-html. Safe here and ONLY here: this SVG is
              generated server-side by segno from a URL WE built
              (api/src/endpoints/campaign_product_qr.py, via
              storefront_path_url), on an authenticated, tenant-scoped
              response -- never user-supplied content. Mirrors the same
              justification on the MFA enrollment QR
              (components/Profile/SecuritySettings.vue). Do not extend
              v-html to any field that can carry user input.
            -->
            <div
              class="product-qr-image d-flex justify-center py-2"
              data-testid="product-qr-image"
              v-html="svg"
            ></div>

            <!--
              Explanatory text -- MUST name store, product, and the chosen
              color explicitly (Jake's spec: "do not be vague"). A code
              that may be printed on physical signage needs the person
              handing it out to be certain what it opens.
            -->
            <p
              class="text-body-2 text-center mb-2"
              data-testid="product-qr-explainer"
            >
              This code opens <strong>{{ productName }}</strong> in
              <strong>{{ selectedColor || "the default color" }}</strong>
              on the <strong>{{ storeName }}</strong> store.
            </p>

            <!--
              Copyable URL fallback -- a QR that will not scan (smudged
              signage, poor lighting) needs a fallback the shopper (or the
              person handing out the code) can read and type/tap.
            -->
            <v-text-field
              :model-value="url"
              readonly
              density="compact"
              variant="outlined"
              label="Link"
              data-testid="product-qr-url"
              append-inner-icon="content_copy"
              @click:append-inner="copyUrl"
            />
            <div
              v-if="copied"
              class="text-caption text-success text-center"
              data-testid="product-qr-copied"
            >
              Copied!
            </div>
          </template>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="py-3 px-3">
          <v-spacer></v-spacer>
          <v-btn
            variant="outlined"
            @click="localOpen = false"
            data-testid="product-qr-close"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  props: {
    open: { type: Boolean, default: false },
    campaignProductId: { type: [Number, String], required: true },
    productName: { type: String, default: "" },
    storeName: { type: String, default: "" },
    // Ordered list of the store's chosen colors for this product
    // (mirrors ProductCard's own `visibleSwatches` sku list) -- drives the
    // color <select>. The FIRST entry is the initial selection so the
    // dialog opens already showing a working code, matching the
    // backend's own "absent color -> first selected_color_skus" fallback.
    colors: { type: Array, default: () => [] },
  },

  emits: ["update:open"],

  data() {
    return {
      selectedColor: null,
      svg: "",
      url: "",
      loading: false,
      error: "",
      copied: false,
    };
  },

  computed: {
    localOpen: {
      get() {
        return this.open;
      },
      set(val) {
        this.$emit("update:open", val);
      },
    },

    colorOptions() {
      return Array.isArray(this.colors) ? this.colors : [];
    },
  },

  watch: {
    // immediate: true so a caller that mounts the dialog already-open
    // (e.g. a future deep-link-triggered open) still fetches -- not just
    // the false->true transition ProductCard's own open/close flow uses.
    open: {
      immediate: true,
      handler(isOpen) {
        if (isOpen) {
          this.copied = false;
          if (!this.selectedColor && this.colorOptions.length > 0) {
            this.selectedColor = this.colorOptions[0];
          }
          this.fetchQr();
        }
      },
    },
  },

  methods: {
    async fetchQr() {
      this.loading = true;
      this.error = "";
      this.copied = false;
      try {
        const params = {};
        if (this.selectedColor) {
          params.color = this.selectedColor;
        }
        const resp = await this.$axios.get(
          `/campaign_product/${this.campaignProductId}/qr`,
          { params }
        );
        this.svg = resp.data.svg;
        this.url = resp.data.url;
      } catch (err) {
        this.error = "Could not load the QR code. Please try again.";
        this.$store.dispatch("raiseError", err);
      } finally {
        this.loading = false;
      }
    },

    async copyUrl() {
      if (!this.url) {
        return;
      }
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(this.url);
        } else {
          const el = document.createElement("textarea");
          el.value = this.url;
          el.style.position = "fixed";
          el.style.opacity = "0";
          document.body.appendChild(el);
          el.select();
          document.execCommand("copy");
          document.body.removeChild(el);
        }
        this.copied = true;
      } catch (err) {
        this.$store.dispatch("raiseError", "Failed to copy link");
      }
    },
  },
};
</script>

<style scoped>
.product-qr-image :deep(svg) {
  max-width: 240px;
  width: 100%;
  height: auto;
}
</style>
